import React from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import { useCases } from '../../contexts/api-contexts/CasesContext';

interface CaseDiagnosesSummaryProps {
  isCollapsed?: boolean;
  isActive?: boolean;
}

export const CaseDiagnosesSummary: React.FC<CaseDiagnosesSummaryProps> = ({
  isCollapsed = false,
  isActive = false,
}) => {
  const { currentCase } = useCases();
  const backgroundColor = isActive ? 'rgba(0, 150, 136, 0.12)' : 'transparent';

  // Helper function to format count with 99+ limit
  const formatCount = (count: number): string => {
    return count >= 99 ? '99+' : count.toString();
  };

  const getSummaryText = (): { text: string; count: string | null } => {
    // Check if we have symptoms selected
    if (
      !currentCase?.selected_symptoms ||
      currentCase.selected_symptoms.length === 0
    ) {
      return {
        text: 'Please select symptoms first to get diagnoses',
        count: null,
      };
    }

    // Count selected diagnoses
    const selectedDiagnosesCount = currentCase?.selected_diagnoses?.length || 0;

    // Count potential diagnoses
    const potentialDiagnosesCount = currentCase?.diagnoses?.length || 0;

    if (selectedDiagnosesCount === 0) {
      return {
        text: `potential diagnoses available`,
        count: formatCount(potentialDiagnosesCount),
      };
    }

    return {
      text: `out of ${formatCount(
        potentialDiagnosesCount
      )} potential diagnoses chosen`,
      count: formatCount(selectedDiagnosesCount),
    };
  };

  // Collapsed view - just show icon
  if (isCollapsed) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 1.5,
          backgroundColor,
          borderLeft: isActive ? '3px solid #009688' : 'none',
        }}
      >
        <IconButton size="small" color={isActive ? 'primary' : 'default'}>
          <MedicalInformationIcon />
        </IconButton>
      </Box>
    );
  }

  // Expanded view
  return (
    <Box
      sx={{
        p: 2,
        backgroundColor,
        borderLeft: isActive ? '3px solid #009688' : 'none',
      }}
    >
      <Box display="flex" alignItems="center" mb={1}>
        <MedicalInformationIcon
          color={isActive ? 'primary' : 'inherit'}
          sx={{ mr: 1 }}
        />
        <Typography
          variant="subtitle1"
          color={isActive ? 'primary' : 'inherit'}
        >
          Diagnoses
        </Typography>
      </Box>
      <Typography
        variant="body2"
        color="text.secondary"
        component="div"
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        {getSummaryText().count && (
          <Chip
            label={getSummaryText().count}
            sx={{
              bgcolor: '#00c853',
              color: 'white',
              fontWeight: 'bold',
              mr: 1,
              '&:hover': {
                bgcolor: '#00a045',
                cursor: 'pointer',
              },
            }}
            size="small"
          />
        )}
        {getSummaryText().text}
      </Typography>
    </Box>
  );
};

export default CaseDiagnosesSummary;
